import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  model,
  Optional,
  signal,
  viewChild,
} from '@angular/core';
import { TranslocoDirective, TranslocoService } from '@ngneat/transloco';
import { UntilDestroy } from '@ngneat/until-destroy';
import { ControlContainer, FormsModule, NgForm } from '@angular/forms';
import { InputTextComponent } from '@pedix-workspace/angular-ui-form-reactive';
import { IconCloseComponent, IconPriceDiscountComponent } from '@pedix-workspace/angular-ui-icons';
import { ButtonComponent } from '@pedix-workspace/angular-ui-button';
import { CurrencyFormatPipe } from '@pedix-workspace/pedixapp-shared-pipes';
import {
  CartItem,
  CouponConfigurationResponse,
  FunctionResponseClient,
  PaymentMethod,
  ValidateCouponParams,
} from '@pedix-workspace/utils';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { CouponValidationService } from '@pedix-workspace/pedixapp-core-services';
import { ApiV1_Category, ApiV1_ProductTag } from '@pedix-workspace/api-v1';
import { CouponValidationErrorsDialogComponent } from '../../coupon-validation-errors-dialog/coupon-validation-errors-dialog.component';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { AlertComponent } from '@pedix-workspace/angular-ui-alert';

@UntilDestroy()
@Component({
    selector: 'pxw-end-order-coupon-fields',
    templateUrl: './end-order-coupon-fields.component.html',
    styleUrls: ['./end-order-coupon-fields.component.scss'],
    providers: [],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        TranslocoDirective,
        FormsModule,
        InputTextComponent,
        IconCloseComponent,
        ButtonComponent,
        CurrencyFormatPipe,
        CouponValidationErrorsDialogComponent,
        AlertComponent,
        IconPriceDiscountComponent,
    ],
    viewProviders: [
        {
            provide: ControlContainer,
            deps: [[Optional, NgForm]],
            useFactory: (ngForm: NgForm) => ngForm,
        },
    ]
})
export class EndOrderCouponFieldsComponent {
  private functions = inject(Functions);
  private couponValidationService = inject(CouponValidationService);
  private toastrService = inject(ToastrService);
  private t = inject(TranslocoService);
  private logger = inject(NGXLogger);

  readonly couponValidationErrorsDialog = viewChild.required(CouponValidationErrorsDialogComponent);

  readonly couponDiscount = input.required<number | null>();
  readonly cartItems = input.required<CartItem[]>();
  readonly paymentMethods = input.required<PaymentMethod[]>();
  readonly paymentMethod = input.required<PaymentMethod | null>();
  readonly categories = input.required<ApiV1_Category[]>();
  readonly productTags = input.required<ApiV1_ProductTag[]>();
  readonly establishmentId = input.required<string>();

  readonly isFormSubmitted = input<boolean>(false);

  readonly couponConfigurationSnapshot = model.required<CouponConfigurationResponse | null>();
  readonly couponCode = model.required<string | null>();

  readonly validatingCoupon = signal(false);

  async validateCoupon($event?: Event) {
    $event?.preventDefault();

    const sanitizedCouponCode = (this.couponCode() || '').trim();
    const hasValidCoupon = sanitizedCouponCode.length > 0;

    if (hasValidCoupon) {
      this.validatingCoupon.set(true);

      const validateCouponFn = httpsCallable<
        ValidateCouponParams,
        FunctionResponseClient<CouponConfigurationResponse>
      >(this.functions, 'validateCoupon');

      try {
        const result = await validateCouponFn({
          establishmentId: this.establishmentId(),
          couponCode: sanitizedCouponCode,
        });
        const couponConfigurationSnapshot = result.data as CouponConfigurationResponse;
        const couponValidationResults = await this.couponValidationService.validateCouponDiscounts({
          cartItems: this.cartItems(),
          paymentMethods: this.paymentMethods() || [],
          selectedPaymentMethod: this.paymentMethod(),
          couponConfiguration: couponConfigurationSnapshot,
          categories: this.categories(),
          productTags: this.productTags(),
        });

        if (couponValidationResults === true) {
          this.couponConfigurationSnapshot.set(couponConfigurationSnapshot);
        } else {
          this.couponValidationErrorsDialog().open(couponValidationResults);

          this.removeCoupon();
        }
      } catch (error: any) {
        this.logger.error('end-order-component::validateCoupon', error);

        if (error.code === 'NOT_FOUND') {
          this.toastrService.error(this.t.translate('endOrder.couponCodeInvalidOrExpiredError'));
        } else {
          this.toastrService.error(this.t.translate('endOrder.couponCodeInvalidOrExpiredError'));
        }

        this.removeCoupon();
      }
      this.validatingCoupon.set(false);
    } else {
      this.toastrService.error(this.t.translate('endOrder.couponCodeInvalidError'));

      this.removeCoupon();
    }
  }

  removeCoupon() {
    this.couponCode.set(null);

    this.couponConfigurationSnapshot.set(null);
  }

  onCouponCodeChange(couponCode: string) {
    this.couponCode.set(couponCode ? couponCode.toUpperCase() : null);
  }
}
